import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  cancelAdminActionFlow,
  executeAdminAction,
  getAdminUser,
  planAdminAction,
} from "../api/adminClient";
import {
  fetchAssignmentCompanies,
} from "../api/reportProfilesClient";
import { openReportDocument } from "./openReportDocument";
import { ROUTES } from "./routes";
import ProfileAssignmentForm, {
  assignmentPrefsToPlanPatch,
  defaultAssignmentPrefs,
} from "./ProfileAssignmentForm";
import DuplicateEmailConfirmDialog from "./DuplicateEmailConfirmDialog";
import { reportBlockLabel } from "./reportBlockLabels";
import InviteEmailSection from "./InviteEmailSection";
import InviteAssessmentOptions from "./InviteAssessmentOptions";
import {
  INVITE_ROLES,
  participantAssessmentUrl,
  workspaceLiteInviteAvailable,
} from "./inviteConstants";
import { useWorkspaceProductDoc } from "./useWorkspaceProductDoc";
import InviteRoleField from "./InviteRoleField";
import {
  ASSESSMENT_MODE_FULL_ONLY,
  assessmentModeLabel,
  defaultInviteOptions,
} from "./inviteOptions";
import {
  PACK_GENERIC,
  buildDefaultEmailHtml,
  buildDefaultEmailHtmlForSend,
  emailTemplateLabel,
  inviteSubjectForPack,
  normalizeEmailPack,
  templateForRole,
  wrapEmailHtmlForApi,
} from "./inviteEmailTemplates";
import {
  AlertStrip,
  IconBuilding,
  IconDocument,
  IconUsers,
  WorkflowStepper,
  formatParagraphs,
} from "./assistantUi";

function workspaceEmailPack() {
  const user = getAdminUser();
  if (user?.email_pack === PACK_GENERIC || user?.tenant_id) return PACK_GENERIC;
  return normalizeEmailPack(user?.email_pack);
}

function workspaceProductName() {
  const user = getAdminUser();
  return (
    user?.product_display_name?.trim() ||
    user?.tenant_name?.trim() ||
    "Team Assessment"
  );
}

function emailBuildOpts(assessmentMode) {
  return {
    assessmentMode,
    pack: workspaceEmailPack(),
    productName: workspaceProductName(),
  };
}

export const QUICK_ACTIONS = [
  {
    id: "invite",
    label: "Send invites",
    description: "Add participants and email assessment links",
    template: "Invite participant@company.com to Company Name",
    icon: IconUsers,
  },
  {
    id: "reminder",
    label: "Draft reminder",
    description: "Remind opened-but-incomplete participants (confirm to send)",
    template: "Draft reminder emails for participants who opened but have not completed",
    icon: IconDocument,
  },
  {
    id: "exec",
    label: "Executive summary",
    description: "Generate PDF for a company and role",
    template: "Open executive summary for Company Name as CEO",
    icon: IconDocument,
  },
  {
    id: "team",
    label: "Team report",
    description: "Open the full team assessment PDF",
    template: "Open team report for Company Name",
    icon: IconDocument,
  },
  {
    id: "company",
    label: "Company report",
    description: "Open the company-wide assessment PDF",
    template: "Open company report for Company Name",
    icon: IconBuilding,
  },
  {
    id: "profile",
    label: "Report profile",
    description: "Create a PDF template with blocks, order, and audience",
    template:
      "Create company report profile for Board Member named Board Pack for Acme Corp: intro, pillars, gaps, variance — assign to Acme Corp",
    icon: IconDocument,
  },
  {
    id: "lite",
    label: "Lite report",
    description: "Open the LITE company report PDF",
    template: "Open lite report for Company Name",
    icon: IconDocument,
  },
  {
    id: "pe",
    label: "PE portfolio report",
    description: "Insights portfolio roll-up for 2–4 companies",
    template: "Portfolio report for Company A, Company B",
    icon: IconBuilding,
  },
];

function formatRolesSummary(participants) {
  const roles = [...new Set((participants || []).map((p) => p.designation).filter(Boolean))];
  if (!roles.length) return "";
  if (roles.length === 1) return roles[0];
  return "Mixed";
}

function buildInviteSummary(plan) {
  const participants = plan.participants || [];
  const emails = participants.map((p) => p.email).join(", ");
  const rolesSummary = formatRolesSummary(participants);
  const rolePhrase =
    rolesSummary === "Mixed"
      ? "with mixed roles"
      : rolesSummary
        ? `as ${rolesSummary}`
        : "";
  return `Send ${participants.length} invite(s) for ${plan.company_name}${rolePhrase ? ` ${rolePhrase}` : ""}: ${emails}`;
}

function participantNameParts(p) {
  if (p.first_name?.trim()) {
    return {
      firstName: p.first_name.trim(),
      lastName: (p.last_name || "").trim(),
    };
  }
  const local = (p.email || "").split("@")[0] || "";
  const parts = local.split(/[._+-]+/).filter(Boolean);
  if (!parts.length) return { firstName: "Participant", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0].charAt(0).toUpperCase() + parts[0].slice(1), lastName: "" };
  return {
    firstName: parts[0].charAt(0).toUpperCase() + parts[0].slice(1),
    lastName: parts
      .slice(1)
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(" "),
  };
}

function initParticipantEmail(p, assessmentMode = "lite_to_full") {
  const templateId = templateForRole(p.designation);
  const { firstName, lastName } = participantNameParts(p);
  return {
    emailTemplate: templateId,
    autoSuggestedTemplate: templateId,
    editorHtml: buildDefaultEmailHtml(
      templateId,
      { firstName, lastName },
      emailBuildOpts(assessmentMode)
    ),
    emailExpanded: false,
    templateIsManual: false,
  };
}

function initVerifyState(plan, reply, warnings = []) {
  const normalizedPlan = { ...defaultInviteOptions(), ...plan };
  const emailByParticipant = (normalizedPlan.participants || []).map((p) => ({
    ...p,
    ...initParticipantEmail(p, normalizedPlan.assessment_mode),
  }));
  const mixedTemplates =
    new Set(emailByParticipant.map((row) => row.emailTemplate)).size > 1;
  return {
    kind: "invite",
    plan: normalizedPlan,
    reply,
    warnings,
    emailByParticipant,
    mixedTemplates,
  };
}

function initProfileVerifyState(plan, reply, warnings = []) {
  return {
    kind: "report_profile",
    plan: { ...plan, action: "create_report_profile" },
    reply,
    warnings,
  };
}

function profileVerifySummary(reply) {
  const text = String(reply || "").trim();
  if (!text) return "";
  const cut = text.split(/\n\nSections in order:/i)[0];
  return cut.trim();
}

function ProfileVerifyCard({ verify, busy, onConfirm, onCancel }) {
  const { plan, reply, warnings } = verify;
  const blocks = (plan?.blocks || [])
    .filter((b) => b.enabled !== false)
    .sort((a, b) => a.order - b.order);
  const summaryText = profileVerifySummary(reply);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [assignmentPrefs, setAssignmentPrefs] = useState(() => defaultAssignmentPrefs(plan));

  useEffect(() => {
    setAssignmentPrefs(defaultAssignmentPrefs(plan));
  }, [plan?.assign_to_company, plan?.assign_to_all_companies]);

  useEffect(() => {
    let cancelled = false;
    setLoadingCompanies(true);
    fetchAssignmentCompanies()
      .then((res) => {
        if (!cancelled) setCompanies(res?.data?.companies || []);
      })
      .catch(() => {
        if (!cancelled) setCompanies([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCompanies(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleConfirm = () => {
    if (
      assignmentPrefs.companyScope === "one" &&
      !(assignmentPrefs.companyName || "").trim()
    ) {
      toast.error("Select a company or choose All companies");
      return;
    }
    const patch = assignmentPrefsToPlanPatch(assignmentPrefs, !!plan?.is_team_base);
    onConfirm({ ...plan, ...patch });
  };

  return (
    <div className="rounded-2xl border border-slate-200/90 bg-white p-4 text-sm shadow-sm space-y-3 max-w-full">
      <p className="font-medium text-gray-900">Review report profile</p>
      {summaryText ? (
        <p className="text-gray-600 text-xs leading-relaxed">{summaryText}</p>
      ) : null}
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-gray-700">
        <dt className="text-gray-500">Name</dt>
        <dd className="font-medium">{plan?.name}</dd>
        <dt className="text-gray-500">Type</dt>
        <dd className="font-medium">{plan?.is_team_base ? "Team report" : "Company report"}</dd>
        {plan?.description ? (
          <>
            <dt className="text-gray-500">Description</dt>
            <dd className="text-xs">{plan.description}</dd>
          </>
        ) : null}
      </dl>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Target Role</label>
          <p className="text-sm font-medium text-gray-800 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
            {plan?.target_role || "All roles"}
          </p>
        </div>
        <ProfileAssignmentForm
          idPrefix="verify-assign"
          variant="builder"
          isTeamBase={!!plan?.is_team_base}
          targetRole={plan?.target_role || ""}
          companies={companies}
          loadingCompanies={loadingCompanies}
          value={assignmentPrefs}
          onChange={setAssignmentPrefs}
        />
      </div>
      <div>
        <p className="text-xs font-medium text-gray-700 mb-1">Sections ({blocks.length})</p>
        <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
          {blocks.map((b) => (
            <li key={b.block_id} className="font-medium text-gray-800">
              {reportBlockLabel(b.block_id, b, {
                isFiveX: workspaceEmailPack() !== PACK_GENERIC,
              })}
            </li>
          ))}
        </ol>
      </div>
      {warnings?.length ? (
        <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 space-y-1">
          {warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      ) : null}
      <p className="text-xs text-gray-500">Confirming saves this profile to Report Profiles.</p>
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          className="flex-1 text-sm font-medium text-white bg-[#004f73] rounded-lg px-3 py-2 hover:bg-[#003d5c] disabled:opacity-50"
          disabled={busy}
          onClick={handleConfirm}
        >
          {busy ? "Creating…" : "Create profile"}
        </button>
        <button
          type="button"
          className="text-sm font-medium text-gray-700 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function ProfileCreatedCard({ profile, assignment, onDone }) {
  const name = profile?.name || profile?.profile_id || "Profile";
  const assignNote = (() => {
    if (!assignment) return "";
    if (assignment.all_companies && assignment.count) {
      return ` and assigned to all ${assignment.count} companies`;
    }
    if (assignment.count > 1) {
      return ` and assigned to ${assignment.count} companies`;
    }
    if (assignment.org_id) {
      return ` and assigned to ${assignment.org_id}`;
    }
    const first = assignment.assignments?.[0];
    if (first?.org_id) return ` and assigned to ${first.org_id}`;
    return "";
  })();
  return (
    <div className="rounded-2xl border border-green-200 bg-green-50/50 p-4 text-sm shadow-sm space-y-3">
      <p className="font-medium text-gray-900">Profile created</p>
      <p className="text-gray-600 text-xs">
        <strong>{name}</strong> is saved
        {assignNote}.
      </p>
      <div className="flex gap-2">
        <a
          href={ROUTES.reportProfiles}
          className="flex-1 text-center text-sm font-medium text-white bg-[#004f73] rounded-lg px-3 py-2 hover:bg-[#003d5c]"
        >
          Open Report Profiles
        </a>
        <button
          type="button"
          className="text-sm font-medium text-gray-700 border border-gray-300 rounded-lg px-3 py-2 hover:bg-white"
          onClick={onDone}
        >
          Done
        </button>
      </div>
    </div>
  );
}

function buildParticipantEmailHtml(row) {
  const editorHtml = row.editorHtml || "";
  const body =
    editorHtml.includes("{{first_name}}") || editorHtml.includes("{{last_name}}")
      ? editorHtml.includes("{{first_name}}") || editorHtml.includes("{{last_name}}")
        ? editorHtml
        : buildDefaultEmailHtmlForSend(row.emailTemplate, {
            assessmentMode: row.assessment_mode,
            pack: workspaceEmailPack(),
            productName: workspaceProductName(),
          })
      : editorHtml;
  return wrapEmailHtmlForApi(body);
}

function summarizeInviteEmailResults(emailResults = [], sendEmail = true) {
  if (!sendEmail) {
    return { sent: 0, failed: 0, attempted: false, firstError: "" };
  }
  if (!emailResults.length) {
    return { sent: 0, failed: 0, attempted: true, firstError: "No email delivery attempted" };
  }
  const sent = emailResults.filter((r) => r.sent === true).length;
  const failed = emailResults.length - sent;
  const firstError =
    emailResults.find((r) => r.mode === "error" && r.error)?.error ||
    emailResults.find((r) => r.mode === "no_mail_config")?.mode ||
    "";
  return { sent, failed, attempted: true, firstError };
}

function buildExecuteEmailPayload(verify) {
  if (!verify?.plan?.send_email) return {};
  const rows = verify.emailByParticipant || [];
  const participant_emails = {};
  for (const row of rows) {
    participant_emails[row.email.trim().toLowerCase()] = {
      email_subject: inviteSubjectForPack(workspaceEmailPack()),
      email_html: buildParticipantEmailHtml(row),
    };
  }
  return { participant_emails };
}

function ReportOpenCard({ document, reply, warnings, busy, onOpen, onCancel }) {
  return (
    <div className="rounded-2xl border border-slate-200/90 bg-white p-4 text-sm shadow-sm space-y-3">
      <p className="font-medium text-gray-900">Open report</p>
      <p className="text-gray-600 text-xs leading-relaxed">{reply}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-gray-700">
        <dt className="text-gray-500">Company</dt>
        <dd className="font-medium">{document.company_name}</dd>
        {document.role ? (
          <>
            <dt className="text-gray-500">Role</dt>
            <dd className="font-medium">{document.role}</dd>
          </>
        ) : null}
        <dt className="text-gray-500">Document</dt>
        <dd className="font-medium">{document.label || document.kind}</dd>
      </dl>
      {warnings?.length ? (
        <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 space-y-1">
          {warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      ) : null}
      <p className="text-xs text-gray-500">Opens the PDF in a new browser tab.</p>
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          className="flex-1 text-sm font-medium text-white bg-[#004f73] rounded-lg px-3 py-2 hover:bg-[#003d5c] disabled:opacity-50"
          disabled={busy}
          onClick={onOpen}
        >
          {busy ? "Opening…" : "Open PDF"}
        </button>
        <button
          type="button"
          className="text-sm font-medium text-gray-700 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function RoleSelectCard({ plan, reply, warnings, roleOptions, onContinue, onCancel }) {
  const roles = roleOptions?.length ? roleOptions : INVITE_ROLES;
  const isReport = !!plan?.document;
  const [reportRole, setReportRole] = useState("");
  const [rows, setRows] = useState(() =>
    (plan?.participants || []).map((p) => ({
      ...p,
      designation: p.designation || "",
    }))
  );

  const allChosen = isReport ? !!reportRole : rows.length > 0 && rows.every((r) => r.designation);
  const multiple = !isReport && rows.length > 1;

  const setAllRoles = (role) => {
    setRows((list) => list.map((p) => ({ ...p, designation: role })));
  };

  const setRowRole = (email, role) => {
    setRows((list) =>
      list.map((p) => (p.email === email ? { ...p, designation: role } : p))
    );
  };

  return (
    <div className="rounded-2xl border border-slate-200/90 bg-white p-4 text-sm shadow-sm space-y-3">
      <p className="font-medium text-gray-900">{reply}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-gray-700">
        <dt className="text-gray-500">Company</dt>
        <dd className="font-medium">{plan.company_name}</dd>
      </dl>

      {warnings?.length ? (
        <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 space-y-1">
          {warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      ) : null}

      {multiple ? (
        <>
          <p className="text-xs font-medium text-gray-700">Apply same role to everyone</p>
          <div className="flex flex-wrap gap-1.5">
            {roles.map((role) => (
              <button
                key={role}
                type="button"
                onClick={() => setAllRoles(role)}
                className="px-2.5 py-1 rounded-md border border-gray-200 text-xs font-medium text-gray-700 hover:border-[#004f73] hover:bg-[#004f73]/5"
              >
                {role}
              </button>
            ))}
          </div>
        </>
      ) : null}

      {isReport ? (
        <>
          <p className="text-xs text-gray-500">
            {plan.document?.label || "Executive summary"}
          </p>
          <p className="text-xs font-medium text-gray-700">Perspective role</p>
          <div className="grid grid-cols-1 gap-1.5" role="group" aria-label="Report role">
            {roles.map((role) => (
              <button
                key={role}
                type="button"
                onClick={() => setReportRole(role)}
                className={`w-full text-left px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  reportRole === role
                    ? "border-[#004f73] bg-[#004f73]/10 text-[#004f73]"
                    : "border-gray-200 text-gray-800 hover:border-[#004f73] hover:bg-[#004f73]/5"
                }`}
              >
                {role}
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
      <p className="text-xs font-medium text-gray-700">
        {multiple ? "Role per participant" : "Participant role"}
      </p>
      <ul className="space-y-2">
        {rows.map((p) => (
          <li
            key={p.email}
            className="rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2 space-y-2"
          >
            <div>
              <div className="font-medium text-gray-900 truncate">{p.email}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {[p.first_name, p.last_name].filter(Boolean).join(" ") || "Name from email"}
              </div>
            </div>
            {multiple ? (
              <InviteRoleField
                roleOptions={roles}
                value={p.designation}
                onChange={(role) => setRowRole(p.email, role)}
                showHint={false}
                selectClassName="w-full text-sm rounded-lg border border-gray-300 bg-white px-2 py-1.5 focus:ring-[#004f73] focus:border-[#004f73]"
              />
            ) : (
              <div className="grid grid-cols-1 gap-1.5" role="group" aria-label="Participant role">
                {roles.map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setRowRole(p.email, role)}
                    className={`w-full text-left px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      p.designation === role
                        ? "border-[#004f73] bg-[#004f73]/10 text-[#004f73]"
                        : "border-gray-200 text-gray-800 hover:border-[#004f73] hover:bg-[#004f73]/5"
                    }`}
                  >
                    {role}
                  </button>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
        </>
      )}

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          className="flex-1 text-sm font-medium text-white bg-[#004f73] rounded-lg px-3 py-2 hover:bg-[#003d5c] disabled:opacity-40"
          disabled={!allChosen}
          onClick={() => {
            if (isReport) {
              onContinue({
                company_name: plan.company_name,
                document: { ...plan.document, role: reportRole },
              });
            } else {
              onContinue({
                ...plan,
                participants: rows,
              });
            }
          }}
        >
          {isReport ? "Continue" : "Continue to review"}
        </button>
        <button
          type="button"
          className="text-sm font-medium text-gray-700 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function ParticipantEmailEditor({ row, busy, onUpdate }) {
  const applyTemplate = (templateId, manual = true) => {
    const { firstName, lastName } = participantNameParts(row);
    onUpdate(row.email, {
      emailTemplate: templateId,
      editorHtml: buildDefaultEmailHtml(
        templateId,
        { firstName, lastName },
        emailBuildOpts(row.assessment_mode)
      ),
      templateIsManual: manual,
    });
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
      <div>
        <div className="font-medium text-gray-900 truncate">{row.email}</div>
        <div className="text-xs text-gray-500 mt-0.5">
          {[row.first_name, row.last_name].filter(Boolean).join(" ") || "Name from email"}
          {" · "}
          {row.designation}
          {" · "}
          <span className="text-[#004f73]">{emailTemplateLabel(row.emailTemplate)}</span>
        </div>
      </div>
      <InviteEmailSection
        compact
        participants={[row]}
        recipientEmails={[row.email]}
        selectedTemplate={row.emailTemplate}
        onSelectTemplate={(id) => applyTemplate(id, true)}
        editorHtml={row.editorHtml}
        onEditorChange={(html) => onUpdate(row.email, { editorHtml: html })}
        autoSuggestedTemplate={row.autoSuggestedTemplate}
        templateIsManual={row.templateIsManual}
        onManualSelect={() => onUpdate(row.email, { templateIsManual: true })}
        expanded={row.emailExpanded}
        onToggleExpanded={(expanded) => onUpdate(row.email, { emailExpanded: expanded })}
        hideSendButton
        disabled={busy}
        assessmentMode={row.assessment_mode}
      />
    </div>
  );
}

function InviteSentCard({ invites, copiedInviteId, onCopyLink, onDone }) {
  const rows = invites || [];
  if (!rows.length) return null;

  return (
    <div className="rounded-xl border border-green-200 bg-white p-3.5 text-sm shadow-sm space-y-3">
      <h3 className="font-semibold text-green-700">Invites sent</h3>
      <p className="text-xs text-gray-600">
        Copy each participant&apos;s assessment link if you need to share it manually.
      </p>
      <ul className="space-y-2 max-h-52 overflow-y-auto pr-0.5">
        {rows.map((inv) => {
          const link = participantAssessmentUrl(inv.survey_token);
          const label = [inv.first_name, inv.last_name].filter(Boolean).join(" ") || inv.email;
          return (
            <li
              key={inv.invite_id || inv.email}
              className="border border-gray-100 rounded-lg p-2.5 flex flex-wrap items-center justify-between gap-2 bg-gray-50/50"
            >
              <p className="font-medium text-gray-900 text-xs min-w-0 truncate">
                {label}
                {label !== inv.email ? (
                  <span className="block font-normal text-gray-500 truncate">{inv.email}</span>
                ) : null}
              </p>
              {link ? (
                <button
                  type="button"
                  className="shrink-0 text-xs font-medium text-[#004f73] border border-[#004f73]/30 rounded-lg px-2.5 py-1.5 hover:bg-[#004f73]/5 min-w-[5.5rem]"
                  onClick={() => onCopyLink(link, inv.invite_id)}
                >
                  {copiedInviteId === inv.invite_id ? "Copied" : "Copy link"}
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        className="w-full text-sm font-medium text-white bg-[#004f73] rounded-lg px-3 py-2 hover:bg-[#003d5c]"
        onClick={onDone}
      >
        Done
      </button>
    </div>
  );
}

function InviteVerifyCard({
  verify,
  busy,
  onConfirm,
  onCancel,
  onUpdateParticipantEmail,
  onUpdatePlan,
}) {
  const { plan, reply, warnings, emailByParticipant = [], mixedTemplates } = verify;
  const sendEmail = !!plan?.send_email;
  const allTemplatesReady = emailByParticipant.every((row) => row.emailTemplate != null);
  const assessmentMode = plan?.assessment_mode || "lite_to_full";
  const offerLiteReport = plan?.offer_lite_report || "Y";
  const workspaceProduct = useWorkspaceProductDoc();
  const litePathAvailable = workspaceLiteInviteAvailable(workspaceProduct);

  return (
    <div className="rounded-2xl border border-slate-200/90 bg-white p-4 text-sm shadow-sm space-y-3 max-w-full">
      <p className="font-medium text-gray-900">Review and send</p>
      <p className="text-gray-600 text-xs leading-relaxed">{reply}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-gray-700">
        <dt className="text-gray-500">Company</dt>
        <dd className="font-medium">{plan.company_name}</dd>
        {emailByParticipant.length === 1 ? (
          <>
            <dt className="text-gray-500">Role</dt>
            <dd className="font-medium">{emailByParticipant[0]?.designation || "—"}</dd>
          </>
        ) : (
          <>
            <dt className="text-gray-500">Roles</dt>
            <dd className="font-medium">{formatRolesSummary(emailByParticipant) || "—"}</dd>
          </>
        )}
        <dt className="text-gray-500">Delivery</dt>
        <dd>{sendEmail ? "Send invitation emails" : "Create invites only"}</dd>
        <dt className="text-gray-500">Assessment</dt>
        <dd className="font-medium">
          {assessmentModeLabel(
            litePathAvailable ? assessmentMode : ASSESSMENT_MODE_FULL_ONLY
          )}
        </dd>
        {litePathAvailable && assessmentMode !== ASSESSMENT_MODE_FULL_ONLY ? (
          <>
            <dt className="text-gray-500">LITE report</dt>
            <dd>{offerLiteReport === "Y" ? "Offer after LITE" : "Do not offer"}</dd>
          </>
        ) : null}
        {sendEmail && mixedTemplates ? (
          <>
            <dt className="text-gray-500">Email templates</dt>
            <dd className="font-medium">One per participant (by role)</dd>
          </>
        ) : null}
      </dl>

      {warnings?.length ? (
        <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 space-y-1">
          {warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      ) : null}

      <InviteAssessmentOptions
        compact
        litePathAvailable={litePathAvailable}
        assessmentMode={
          litePathAvailable ? assessmentMode : ASSESSMENT_MODE_FULL_ONLY
        }
        offerLiteReport={litePathAvailable ? offerLiteReport : "N"}
      />

      {sendEmail ? (
        <div className="space-y-3">
          <p className="text-xs font-medium text-gray-700">
            {emailByParticipant.length > 1
              ? "Invitation email for each participant"
              : "Invitation email"}
          </p>
          {emailByParticipant.map((row) => (
            <ParticipantEmailEditor
              key={row.email}
              row={row}
              busy={busy}
              onUpdate={onUpdateParticipantEmail}
            />
          ))}
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
          {emailByParticipant.map((p) => (
            <li key={p.email} className="px-3 py-2 bg-gray-50/50">
              <div className="font-medium text-gray-900 truncate">{p.email}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {[p.first_name, p.last_name].filter(Boolean).join(" ") || "Name from email"}
                {" · "}
                {p.designation}
              </div>
            </li>
          ))}
        </ul>
      )}

      {sendEmail ? (
        <p className="text-xs text-gray-500">
          Confirming will create invites and send emails. This cannot be undone.
        </p>
      ) : null}

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          className="flex-1 text-sm font-medium text-white bg-[#004f73] rounded-lg px-3 py-2 hover:bg-[#003d5c] disabled:opacity-50"
          disabled={busy || (sendEmail && !allTemplatesReady)}
          onClick={onConfirm}
        >
          {busy ? "Working…" : sendEmail ? "Confirm & send" : "Confirm & create"}
        </button>
        <button
          type="button"
          className="text-sm font-medium text-gray-700 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

const ActionModePanel = forwardRef(function ActionModePanel(
  {
    disabled = false,
    variant = "legacy",
    inputValue,
    onInputChange,
    hideFooterInput = false,
    onWorkflowActivityChange,
    onPlanResult,
    onWorkflowComplete,
    suppressInlineHints = false,
    chatContext = null,
  },
  ref
) {
  const isCommand = variant === "command";
  const [internalInput, setInternalInput] = useState("");
  const input = inputValue !== undefined ? inputValue : internalInput;
  const setInput = onInputChange ?? setInternalInput;
  const [messages, setMessages] = useState([]);
  const [flowHint, setFlowHint] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const [pendingRole, setPendingRole] = useState(null);
  const [pendingReport, setPendingReport] = useState(null);
  const [pendingVerify, setPendingVerify] = useState(null);
  const [duplicateConfirm, setDuplicateConfirm] = useState(null);
  const [actionDraft, setActionDraft] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [profileResult, setProfileResult] = useState(null);
  const [actionFlowId, setActionFlowId] = useState(null);
  const [copiedInviteId, setCopiedInviteId] = useState(null);
  const scrollEndRef = useRef(null);

  const flowBlocked =
    !!pendingRole || !!pendingReport || !!pendingVerify || !!sendResult || !!profileResult;

  const workflowMeta = useMemo(() => {
    if (sendResult || profileResult) return { step: 3, total: 3, label: "Complete" };
    if (pendingVerify) {
      return {
        step: 3,
        total: 3,
        label: pendingVerify.kind === "report_profile" ? "Confirm profile" : "Confirm & send",
      };
    }
    if (pendingRole || pendingReport) return { step: 2, total: 3, label: "Review" };
    if (loading) return { step: 1, total: 3, label: "Planning" };
    return null;
  }, [sendResult, profileResult, pendingVerify, pendingRole, pendingReport, loading]);

  const workflowEngaged = useMemo(() => {
    if (!isCommand) return false;
    const cardActive = !!(
      workflowMeta ||
      sendResult ||
      profileResult ||
      pendingRole ||
      pendingReport ||
      pendingVerify ||
      actionDraft
    );
    if (suppressInlineHints) {
      return cardActive || (loading && !flowBlocked);
    }
    return !!(
      cardActive ||
      (flowHint && flowHint.trim()) ||
      messages.length > 0
    );
  }, [
    isCommand,
    suppressInlineHints,
    workflowMeta,
    sendResult,
    profileResult,
    pendingRole,
    pendingReport,
    pendingVerify,
    actionDraft,
    flowHint,
    messages.length,
    loading,
    flowBlocked,
  ]);

  useEffect(() => {
    onWorkflowActivityChange?.(workflowEngaged);
  }, [workflowEngaged, onWorkflowActivityChange]);

  const canSend = useMemo(
    () => !disabled && !loading && !flowBlocked && input.trim().length > 0,
    [disabled, loading, flowBlocked, input]
  );

  useEffect(() => {
    if (isCommand && suppressInlineHints) return;
    scrollEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [
    isCommand,
    suppressInlineHints,
    messages,
    loading,
    pendingRole,
    pendingReport,
    pendingVerify,
    sendResult,
    profileResult,
  ]);

  const copyInviteLink = async (link, inviteId) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedInviteId(inviteId);
      window.setTimeout(() => setCopiedInviteId(null), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const dismissSendResult = () => {
    setSendResult(null);
    setCopiedInviteId(null);
    setActionFlowId(null);
    clearFlowHint();
  };

  const pushAssistant = (content) => {
    setMessages((prev) => [...prev, { role: "assistant", content }]);
    if (isCommand) setFlowHint(content || "");
  };

  const clearFlowHint = () => setFlowHint("");

  const runWorkflowInstruction = async (message) => {
    const asked = String(message || "").trim();
    if (!asked || disabled || loading) return;
    setInput("");
    onInputChange?.("");
    clearFlowHint();
    setMessages((prev) => [...prev, { role: "user", content: asked }]);
    setLoading(true);
    setPendingRole(null);
    setPendingReport(null);
    setPendingVerify(null);

    const history = [
      ...messages.map((m) => ({
        role: m.role,
        content: m.content,
      })),
      { role: "user", content: asked },
    ];

    try {
      const res = await planAdminAction({
        message: asked,
        draft: actionDraft,
        history,
        flowId: actionFlowId,
        context: chatContext || undefined,
      });
      const data = res?.data || {};
      if (data.flow_id) setActionFlowId(data.flow_id);
      handlePlanResult(data);
    } catch (err) {
      toast.error(err?.message || "Could not plan action");
      pushAssistant("Something went wrong planning that action. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openVerifyStep = (plan, reply, warnings) => {
    setPendingVerify(initVerifyState(plan, reply, warnings));
    setPendingRole(null);
  };

  const openDocumentPdf = async (reportDoc) => openReportDocument(reportDoc);

  const handlePlanResult = (data) => {
    const reply = data.reply || "";
    onPlanResult?.(data);

    if (data.action === "open_report" && data.document) {
      setActionDraft(null);
      pushAssistant(reply);
      setPendingReport({
        document: data.document,
        reply,
        warnings: data.warnings || [],
      });
      return;
    }

    if (data.action === "select_role") {
      const rolePlan =
        data.plan ||
        (data.document
          ? {
              company_name: data.document.company_name,
              document: data.document,
            }
          : null);
      if (rolePlan) {
        setActionDraft(null);
        pushAssistant(reply);
        setPendingRole({
          plan: rolePlan,
          reply,
          warnings: data.warnings || [],
          roleOptions: data.role_options || INVITE_ROLES,
        });
        return;
      }
    }

    if (data.action === "create_report_profile" && data.plan) {
      setActionDraft(null);
      pushAssistant(reply);
      setPendingVerify(initProfileVerifyState(data.plan, reply, data.warnings || []));
      return;
    }

    if (data.action === "send_invites" && data.plan) {
      setActionDraft(null);
      if (data.role_explicit) {
        pushAssistant(reply);
        openVerifyStep(data.plan, reply, data.warnings || []);
      } else {
        pushAssistant(reply || "What role should these participants have?");
        setPendingRole({
          plan: data.plan,
          reply: reply || "What role should these participants have?",
          warnings: data.warnings || [],
          roleOptions: data.role_options || INVITE_ROLES,
        });
      }
      return;
    }

    if (data.action === "clarify") {
      setActionDraft(data.draft || null);
      pushAssistant(reply || "I need a bit more detail to run that action.");
      return;
    }

    if (data.action === "resend_invites" && data.plan?.invite_ids?.length) {
      void (async () => {
        setLoading(true);
        try {
          const res = await executeAdminAction({
            plan: data.plan,
            flow_id: actionFlowId || undefined,
          });
          const msg = res?.message || reply || "Reminder emails sent.";
          pushAssistant(msg);
          setActionDraft(null);
          toast.success(msg);
          onWorkflowComplete?.({ message: msg });
        } catch (err) {
          toast.error(err?.message || "Could not send reminders");
          pushAssistant(err?.message || "Could not send reminders.");
        } finally {
          setLoading(false);
        }
      })();
      return;
    }

    setActionDraft(data.draft || actionDraft);
    pushAssistant(reply || "I need a bit more detail to run that action.");
  };

  const submitInstruction = async () => {
    if (!canSend) return;
    await runWorkflowInstruction(input.trim());
  };

  const confirmReportRole = (payload) => {
    const doc = payload.document;
    if (!doc?.role) return;
    setPendingRole(null);
    setPendingReport({
      document: doc,
      reply: `Ready to open: ${doc.label || doc.kind}`,
      warnings: pendingRole?.warnings || [],
    });
    pushAssistant(`Role: ${doc.role}. Tap Open PDF when ready.`);
  };

  const confirmRoles = (plan) => {
    if (plan?.document) {
      confirmReportRole(plan);
      return;
    }
    if (!plan?.participants?.every((p) => p.designation)) return;
    const summary = buildInviteSummary(plan);
    const userSummary =
      plan.participants.length === 1
        ? plan.participants[0].designation
        : plan.participants
            .map((p) => `${p.email}: ${p.designation}`)
            .join("; ");
    setMessages((prev) => [
      ...prev,
      { role: "user", content: userSummary },
      {
        role: "assistant",
        content:
          plan.participants.length === 1
            ? `Role set to ${plan.participants[0].designation}. Review the details below, customize the email if needed, then confirm.`
            : "Roles set. Review each participant below, customize the email if needed, then confirm.",
      },
    ]);
    openVerifyStep(plan, summary, pendingRole?.warnings || []);
  };

  const updateParticipantEmail = (email, patch) => {
    setPendingVerify((prev) => {
      if (!prev) return prev;
      const emailByParticipant = (prev.emailByParticipant || []).map((row) =>
        row.email === email ? { ...row, ...patch } : row
      );
      return {
        ...prev,
        emailByParticipant,
        mixedTemplates:
          new Set(emailByParticipant.map((row) => row.emailTemplate)).size > 1,
      };
    });
  };

  const updatePlanOptions = (patch) => {
    setPendingVerify((prev) => {
      if (!prev?.plan) return prev;
      const nextPlan = { ...prev.plan, ...patch };
      const modeChanged =
        patch.assessment_mode &&
        patch.assessment_mode !== prev.plan.assessment_mode;
      const nextRows = modeChanged
        ? (prev.emailByParticipant || []).map((row) => {
            const mode = nextPlan.assessment_mode;
            if (row.templateIsManual) {
              return { ...row, assessment_mode: mode };
            }
            const { firstName, lastName } = participantNameParts(row);
            return {
              ...row,
              assessment_mode: mode,
              editorHtml: buildDefaultEmailHtml(
                row.emailTemplate,
                { firstName, lastName },
                emailBuildOpts(mode)
              ),
            };
          })
        : prev.emailByParticipant;
      return { ...prev, plan: nextPlan, emailByParticipant: nextRows };
    });
  };

  const runExecute = async (isProceed = false, planOverride = null) => {
    if (!pendingVerify?.plan && !planOverride) return;
    setLoading(true);

    if (pendingVerify?.kind === "report_profile") {
      const plan = planOverride || pendingVerify.plan;
      try {
        const res = await executeAdminAction({
          plan: { ...plan, action: "create_report_profile" },
          flow_id: actionFlowId || undefined,
        });
        const data = res?.data || {};
        if (data.flow_id) setActionFlowId(data.flow_id);
        pushAssistant(`Done — created report profile "${plan.name}".`);
        setProfileResult({
          profile: data.profile,
          assignment: data.assignment,
        });
        setPendingVerify(null);
        setActionDraft(null);
        toast.success(res.message || "Profile created");
      } catch (err) {
        toast.error(err?.message || "Could not create profile");
      } finally {
        setLoading(false);
      }
      return;
    }

    const plan = pendingVerify.plan;
    const emailPayload = buildExecuteEmailPayload(pendingVerify);

    try {
      const res = await executeAdminAction({
        plan,
        flow_id: actionFlowId || undefined,
        is_proceed: isProceed ? "Y" : "N",
        frontend_origin: window.location.origin,
        ...emailPayload,
      });
      const data = res?.data || {};
      if (data.flow_id) setActionFlowId(data.flow_id);

      if (data.needs_confirmation) {
        setDuplicateConfirm({
          email: data.email || "",
          otherCompanies: data.other_companies || [],
          username: data.username || "",
          confirmationReason: data.confirmation_reason || "other_company",
        });
        setLoading(false);
        return;
      }

      const invites = data.invites || [];
      const count = invites.length || plan.participants?.length || 0;
      const sendEmail = data.send_email !== false && plan.send_email !== false;
      const emailStats = summarizeInviteEmailResults(data.email_results || [], sendEmail);
      let doneMsg;
      if (!sendEmail) {
        doneMsg = `Done — created ${count} invite(s). Copy assessment links below if needed.`;
      } else if (emailStats.sent > 0 && emailStats.failed === 0) {
        doneMsg = `Done — created ${count} invite(s) and sent ${emailStats.sent} invitation email(s).`;
      } else if (emailStats.sent > 0) {
        doneMsg = `Done — created ${count} invite(s). Sent ${emailStats.sent} of ${emailStats.sent + emailStats.failed} emails; some failed.`;
      } else {
        doneMsg = `Done — created ${count} invite(s), but emails did not send. Copy assessment links below.`;
      }
      pushAssistant(doneMsg);
      onWorkflowComplete?.({ message: doneMsg, reset: false });
      setSendResult({ invites });
      setPendingVerify(null);
      setPendingRole(null);
      setDuplicateConfirm(null);
      setActionDraft(null);
      if (!sendEmail) {
        toast.success(res.message || "Invites created");
      } else if (emailStats.sent > 0 && emailStats.failed === 0) {
        toast.success(res.message || "Invites sent");
      } else {
        toast.error(
          emailStats.firstError ||
            "Email delivery failed — check MAIL settings on the server (Gmail needs an app password)."
        );
      }
    } catch (err) {
      toast.error(err?.message || "Could not complete action");
    } finally {
      setLoading(false);
    }
  };

  const runOpenReport = async () => {
    if (!pendingReport?.document) return;
    setLoading(true);
    const doc = pendingReport.document;
    try {
      const openResult = await openDocumentPdf(doc);
      await executeAdminAction({
        document: doc,
        flow_id: actionFlowId || undefined,
      });
      const label = doc.label || "report";
      let completeMsg;
      if (openResult?.downloaded) {
        completeMsg = `Your browser blocked a new tab — ${label} was downloaded instead. Check your downloads folder.`;
        toast.success("Report downloaded");
      } else {
        completeMsg = `Opened ${label} in a new tab.`;
        toast.success("Report opened");
      }
      pushAssistant(completeMsg);
      onWorkflowComplete?.({ message: completeMsg });
      setPendingReport(null);
      setActionFlowId(null);
    } catch (err) {
      const msg = err?.message || "Could not open report";
      toast.error(msg);
      pushAssistant(msg);
      setPendingReport(null);
      setActionFlowId(null);
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      submit: submitInstruction,
      submitWithMessage: (message) => runWorkflowInstruction(message),
      clearFlowHint,
      resetWorkflow,
      focusInput: () => {
        (inputRef.current ?? document.getElementById("command-center-input"))?.focus();
      },
      isLoading: () => loading,
      isWorkflowEngaged: () => workflowEngaged,
      isAwaitingWorkflowConfirm: () => actionDraft?.awaiting === "confirm",
      runOpenReport,
    }),
    [
      canSend,
      input,
      loading,
      flowBlocked,
      disabled,
      messages,
      actionDraft,
      actionFlowId,
      workflowEngaged,
      pendingReport,
    ]
  );

  const resetWorkflow = () => {
    setMessages([]);
    setFlowHint("");
    setInput("");
    onInputChange?.("");
    setLoading(false);
    setPendingRole(null);
    setPendingReport(null);
    setPendingVerify(null);
    setDuplicateConfirm(null);
    setActionDraft(null);
    setSendResult(null);
    setProfileResult(null);
    setCopiedInviteId(null);
    setActionFlowId(null);
  };

  const dismissProfileResult = () => {
    setProfileResult(null);
    setActionFlowId(null);
    clearFlowHint();
  };

  const cancelFlow = () => {
    const flowId = actionFlowId;
    const atStep = pendingVerify
      ? pendingVerify.kind === "report_profile"
        ? "verify_profile"
        : "verify"
      : pendingReport
        ? "open_report"
        : pendingRole
          ? "select_role"
          : "chat";
    const cancelledProfile = pendingVerify?.kind === "report_profile";
    if (isCommand) {
      setFlowHint(
        cancelledProfile
          ? "Workflow cancelled. Profile was not created."
          : pendingReport || (actionDraft?.document_kind && !pendingVerify)
            ? "Workflow cancelled. Report was not opened."
            : "Workflow cancelled. No invites were created."
      );
    } else {
      pushAssistant(
        cancelledProfile
          ? "Cancelled — profile was not created."
          : pendingReport || (actionDraft?.document_kind && !pendingVerify)
            ? "Cancelled — report was not opened."
            : "Cancelled — no invites were created."
      );
    }
    setPendingVerify(null);
    setPendingReport(null);
    setPendingRole(null);
    setActionDraft(null);
    setSendResult(null);
    setProfileResult(null);
    setCopiedInviteId(null);
    setActionFlowId(null);
    if (flowId) {
      cancelAdminActionFlow({ flowId, atStep }).catch(() => {
        /* audit only — ignore */
      });
    }
  };

  if (isCommand) {
    return (
      <>
        <div className="flex flex-col gap-4 min-h-0">
          {workflowMeta ? (
            <WorkflowStepper
              step={workflowMeta.step}
              total={workflowMeta.total}
              label={workflowMeta.label}
            />
          ) : null}

          {(loading && !flowBlocked) ||
          (!suppressInlineHints &&
            flowHint &&
            !pendingRole &&
            !pendingReport &&
            !pendingVerify &&
            !sendResult) ? (
            <AlertStrip>
              {loading && !flowHint
                ? "Planning your workflow…"
                : formatParagraphs(flowHint).map((p, i) => (
                    <p key={i} className={i > 0 ? "mt-2" : ""}>
                      {p}
                    </p>
                  ))}
            </AlertStrip>
          ) : null}

          {pendingReport ? (
              <ReportOpenCard
                document={pendingReport.document}
                reply={pendingReport.reply}
                warnings={pendingReport.warnings}
                busy={loading}
                onOpen={runOpenReport}
                onCancel={cancelFlow}
              />
            ) : null}

            {pendingRole ? (
              <RoleSelectCard
                plan={pendingRole.plan}
                reply={pendingRole.reply}
                warnings={pendingRole.warnings}
                roleOptions={pendingRole.roleOptions}
                onContinue={confirmRoles}
                onCancel={cancelFlow}
              />
            ) : null}

            {pendingVerify ? (
              pendingVerify.kind === "report_profile" ? (
                <ProfileVerifyCard
                  verify={pendingVerify}
                  busy={loading}
                  onConfirm={(mergedPlan) => runExecute(false, mergedPlan)}
                  onCancel={cancelFlow}
                />
              ) : (
                <InviteVerifyCard
                  verify={pendingVerify}
                  busy={loading}
                  onConfirm={() => runExecute(false)}
                  onCancel={cancelFlow}
                  onUpdateParticipantEmail={updateParticipantEmail}
                  onUpdatePlan={updatePlanOptions}
                />
              )
            ) : null}

            {sendResult ? (
              <InviteSentCard
                invites={sendResult.invites}
                copiedInviteId={copiedInviteId}
                onCopyLink={copyInviteLink}
                onDone={dismissSendResult}
              />
            ) : null}

            {profileResult ? (
              <ProfileCreatedCard
                profile={profileResult.profile}
                assignment={profileResult.assignment}
                onDone={dismissProfileResult}
              />
            ) : null}

          <div ref={scrollEndRef} className="h-px shrink-0" aria-hidden />

          {!hideFooterInput && !flowBlocked && !sendResult && !profileResult ? (
            <div className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Custom instruction
              </p>
              <p className="text-xs text-slate-500 -mt-1">
                Replace placeholders with real company names and emails, then start the workflow.
              </p>
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  disabled={disabled || loading}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Invite name@company.com to Acme as CFO…"
                  className="flex-1 bg-slate-50 border border-slate-200 text-slate-900 text-sm rounded-xl focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40 px-3.5 py-2.5 disabled:opacity-60"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      submitInstruction();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={submitInstruction}
                  disabled={!canSend}
                  className="shrink-0 text-sm font-semibold text-white bg-[#004f73] hover:bg-[#003d5c] disabled:opacity-40 rounded-xl px-5 py-2.5 transition-colors"
                >
                  {loading ? "…" : "Start"}
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <DuplicateEmailConfirmDialog
          open={!!duplicateConfirm}
          email={duplicateConfirm?.email}
          otherCompanies={duplicateConfirm?.otherCompanies}
          targetCompanyName={pendingVerify?.plan?.company_name}
          username={duplicateConfirm?.username}
          confirmationReason={duplicateConfirm?.confirmationReason}
          busy={loading}
          onConfirm={() => runExecute(true)}
          onCancel={() => setDuplicateConfirm(null)}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50/90">
          <div className="flex flex-col justify-end min-h-full px-3 py-4 gap-3">
            {!messages.length && !loading && !flowBlocked ? (
              <p className="text-sm text-gray-500 leading-relaxed">
                Describe a task in plain English — send invites, open reports, or create a report
                profile with name, role, blocks, order, and company assignment.
              </p>
            ) : null}

            {flowHint && !pendingRole && !pendingReport && !pendingVerify && !sendResult ? (
              <AlertStrip>
                {formatParagraphs(flowHint).map((p, i) => (
                  <p key={i} className={i > 0 ? "mt-2" : ""}>
                    {p}
                  </p>
                ))}
              </AlertStrip>
            ) : null}

            {pendingReport ? (
              <ReportOpenCard
                document={pendingReport.document}
                reply={pendingReport.reply}
                warnings={pendingReport.warnings}
                busy={loading}
                onOpen={runOpenReport}
                onCancel={cancelFlow}
              />
            ) : null}

            {pendingRole ? (
              <RoleSelectCard
                plan={pendingRole.plan}
                reply={pendingRole.reply}
                warnings={pendingRole.warnings}
                roleOptions={pendingRole.roleOptions}
                onContinue={confirmRoles}
                onCancel={cancelFlow}
              />
            ) : null}

            {pendingVerify ? (
              pendingVerify.kind === "report_profile" ? (
                <ProfileVerifyCard
                  verify={pendingVerify}
                  busy={loading}
                  onConfirm={(mergedPlan) => runExecute(false, mergedPlan)}
                  onCancel={cancelFlow}
                />
              ) : (
                <InviteVerifyCard
                  verify={pendingVerify}
                  busy={loading}
                  onConfirm={() => runExecute(false)}
                  onCancel={cancelFlow}
                  onUpdateParticipantEmail={updateParticipantEmail}
                  onUpdatePlan={updatePlanOptions}
                />
              )
            ) : null}

            {sendResult ? (
              <InviteSentCard
                invites={sendResult.invites}
                copiedInviteId={copiedInviteId}
                onCopyLink={copyInviteLink}
                onDone={dismissSendResult}
              />
            ) : null}

            {profileResult ? (
              <ProfileCreatedCard
                profile={profileResult.profile}
                assignment={profileResult.assignment}
                onDone={dismissProfileResult}
              />
            ) : null}

            <div ref={scrollEndRef} className="h-px shrink-0" aria-hidden />
          </div>
        </div>

        <div className="shrink-0 border-t border-gray-200 bg-white px-3 py-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              disabled={disabled || loading || flowBlocked}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. Invite jane@acme.com to Acme as CFO…"
              className="flex-1 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-[#004f73] focus:border-[#004f73] px-3 py-2 disabled:bg-gray-50"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submitInstruction();
                }
              }}
            />
            <button
              type="button"
              onClick={submitInstruction}
              disabled={!canSend}
              className="shrink-0 text-sm font-medium text-white bg-[#004f73] border border-[#004f73] rounded-lg px-3 py-2 hover:bg-[#003d5c] disabled:opacity-60"
            >
              {loading ? "…" : "Run"}
            </button>
          </div>
        </div>
      </div>

      <DuplicateEmailConfirmDialog
        open={!!duplicateConfirm}
        email={duplicateConfirm?.email}
        otherCompanies={duplicateConfirm?.otherCompanies}
        targetCompanyName={pendingVerify?.plan?.company_name}
        username={duplicateConfirm?.username}
        confirmationReason={duplicateConfirm?.confirmationReason}
        busy={loading}
        onConfirm={() => runExecute(true)}
        onCancel={() => setDuplicateConfirm(null)}
      />
    </>
  );
});

export default ActionModePanel;
